import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { CONSENT_EVENT } from "@/components/DeferredAnalytics";


const STORAGE_KEY = "soxie365_consent";
const OPEN_EVENT = "soxie365:open-cookie-preferences";
/** 6 months. */
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 183;

type Choice = "accepted" | "rejected";
type Stored = { choice: Choice; at: number };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function readStored(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (parsed.choice !== "accepted" && parsed.choice !== "rejected") return null;
    if (!parsed.at || Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function applyConsent(choice: Choice) {
  const granted = choice === "accepted" ? "granted" : "denied";
  window.dataLayer = window.dataLayer || [];
  const gtag =
    window.gtag ??
    ((...args: unknown[]) => {
      window.dataLayer!.push(args);
    });
  gtag("consent", "update", {
    analytics_storage: granted,
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  // Only now is it legal (and worth the main-thread cost) to fetch gtag.js.
  if (choice === "accepted") window.dispatchEvent(new CustomEvent(CONSENT_EVENT));
}


/** Re-opens the banner from anywhere (e.g. the cookie policy page or footer). */
export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (stored) applyConsent(stored.choice);
    else setVisible(true);

    const onOpen = () => setVisible(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const decide = useCallback((choice: Choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, at: Date.now() } satisfies Stored));
    } catch {}
    applyConsent(choice);
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] animate-in slide-in-from-bottom duration-300"
    >
      <div className="mx-auto flex max-w-7xl justify-start px-4 pb-4 sm:px-6 sm:pb-6">
        <div className="pointer-events-auto w-full max-w-3xl border-2 border-accent bg-black p-4 text-white shadow-[0_0_40px_rgba(57,255,20,0.15)] sm:p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-xl">
              <p className="font-mono text-[10px] uppercase tracking-widest text-accent">
                [ COOKIES ]
              </p>
              <p className="mt-2 text-sm leading-relaxed text-neutral-300">
                We use strictly necessary cookies to run the shop and checkout. With your permission
                we'd also use analytics cookies to see which pages work. Nothing is tracked until you
                choose. See our{" "}
                <Link to="/cookies" className="text-white underline underline-offset-4 hover:text-accent">
                  Cookie Policy
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-white underline underline-offset-4 hover:text-accent">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row md:flex-col lg:flex-row">
              <button
                type="button"
                onClick={() => decide("rejected")}
                className="border-2 border-white/30 px-6 py-3 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:border-white"
              >
                Reject non-essential
              </button>
              <button
                type="button"
                onClick={() => decide("accepted")}
                className="border-2 border-accent bg-accent px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-black transition-transform hover:scale-[1.02]"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
